/**
 * Upright Agent Chat (/chat route): assistant-ui Thread + LocalRuntime backed by
 * a server-side chat endpoint (runs the Anthropic tool-use loop). Styled via
 * CSS-variable overrides mapped to Upright design tokens.
 *
 * Thin orchestrator; the pieces live in sibling modules: upright-chat-shared
 * (state/types/helpers), -ui-common, -tool-uis, -adapter, -welcome, -composer.
 */
import '@assistant-ui/react-ui/styles/index.css';
import '@assistant-ui/react-ui/styles/themes/default.css';
import './upright-chat-theme.css';
// Print CSS for /chat (PLT-4888): all rules are @media print, scoped to
// body.upright-chat-page. The unscopeable @page rule is injected by ChatView on
// mount instead (as a global rule it broke /render/chart PDF exports).
import './upright-chat-print.css';

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Thread } from '@assistant-ui/react-ui';
import { ExportedMessageRepository, useAssistantRuntime, useThread } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { ChatContextCard } from './chat-context-card';
import { useConversationEvents } from './use-conversation-events';
import { fetchChatConversationDetail } from './history/chat-history-data';
import { buildSeededRepository } from './history/seed-message';
import { getChatHistoryState, subscribeChatHistoryState } from './history/chat-history-state';
import { UprightComposer } from './upright-chat-composer';
import { UprightChatDebugPanel } from './upright-chat-debug-panel';
import { MarkdownText, MarkdownTextNonTeam } from './upright-chat-markdown';
import { useIsTeam } from '../common/generic/hooks';
import {
  composerPlaceholder,
  composerState,
  hiddenEntityContext,
  pendingChatAttachments,
  pendingWebSearchHandOff,
  resetChatLoggingState,
  setSeedingInitialPrompt,
  setWebSearchChoice,
  uprightAttachmentAdapter,
} from './upright-chat-shared';
import { getStickyPageChatBinding, setStickyPageChatBinding } from './current-page-chat-binding';
import { CHAT_TOOL_UIS, MessageSources } from './upright-chat-tool-uis';
import { ToolTrailFallback } from './tool-trail';
import { FOLLOW_THREAD_EVENT, TypingFooter } from './upright-chat-ui-common';
import { UprightChatWelcome, WELCOME_SUGGESTIONS } from './upright-chat-welcome';
import { ChatFeedbackControl } from './feedback/chat-feedback-control';

// Narrowed rather than trusted: this value flows into the backend chat-usage
// log, so a bogus value from a hand-crafted URL would pollute analytics.
const VALID_INITIAL_SOURCES = ['welcome_suggestion', 'followup_suggestion', 'user_input', 'campaign_link'] as const;
type InitialSource = (typeof VALID_INITIAL_SOURCES)[number];
function isValidInitialSource(s: string | null | undefined): s is InitialSource {
  return s !== null && s !== undefined && (VALID_INITIAL_SOURCES as readonly string[]).includes(s);
}
export function parseInitialSource(raw: string | null | undefined): InitialSource | undefined {
  return isValidInitialSource(raw) ? raw : undefined;
}

/**
 * Seeds `prompt` into the thread once on mount. The runtime is shared app-wide
 * (PLT-4625), so an in-progress panel thread may already exist; we switch to a
 * fresh thread first so the seeded prompt doesn't land in an unrelated
 * conversation. Callers that want to continue a thread navigate to /chat without
 * ?prompt= (this component never mounts). `initialSource` tags the auto-sent
 * message so a cmd-clicked welcome pill isn't miscounted as plain user_input.
 */
function InitialPromptAutoSender({ prompt, initialSource }: { prompt: string; initialSource?: InitialSource }) {
  const runtime = useAssistantRuntime();
  useEffect(() => {
    // Capture deep-link bindings by value before resetChatLoggingState wipes
    // them: the entity context + sticky page binding give the model company
    // context (PLT-4995) and drive the visible context chip (PLT-5051). Hand-off
    // files are drained from the slot so a later remount can't re-attach them.
    const preservedHiddenEntity = hiddenEntityContext.value;
    // The sticky binding outlives the page that published it, so on arrival it
    // may name whichever company page the user last visited in this tab rather
    // than anything this launch is about. Keep it only when it names the entity
    // the launch URL bound (chat-view has just set `hiddenEntityContext` from
    // it); an entity-less launch, such as an organisation home's prompt cards,
    // starts with no company (PLT-7354).
    const stickyBinding = getStickyPageChatBinding();
    const preservedStickyBinding =
      stickyBinding !== null &&
      preservedHiddenEntity !== null &&
      stickyBinding.entityId === preservedHiddenEntity.entityId &&
      stickyBinding.entityType === preservedHiddenEntity.entityType
        ? stickyBinding
        : null;
    const handoffFiles = pendingChatAttachments.value;
    pendingChatAttachments.value = [];
    // The launcher's EU web search choice, applied after the reset below, which
    // would otherwise turn it back off (PLT-7271). Drained like the files.
    const handoffWebSearch = pendingWebSearchHandOff.value;
    pendingWebSearchHandOff.value = null;

    // Hold the welcome screen's pills inert until the seeded prompt has landed:
    // until then the thread is empty and idle, so nothing in thread state marks
    // this as a busy moment (PLT-6108).
    setSeedingInitialPrompt(true);

    void (async () => {
      // The flag is cleared in `finally` — a throw on any of the steps below
      // would otherwise leave the welcome pills permanently inert.
      try {
        // Await the thread switch before seeding: switchToNewThread defers the
        // main-thread id update behind a microtask, and appending synchronously
        // raced it, landing the prompt on the old thread (PLT-5240). Safe to await
        // (empty deps = no re-run, no cancellation window).
        try {
          await runtime.threads.switchToNewThread();
        } catch (err) {
          // Fall through and seed the current thread rather than leaving it blank.
          console.error('Agent-first launch: switching to a new chat thread failed; seeding the current thread.', err);
        }
        resetChatLoggingState();
        if (preservedHiddenEntity !== null) {
          hiddenEntityContext.value = preservedHiddenEntity;
        }
        if (preservedStickyBinding !== null) {
          setStickyPageChatBinding(preservedStickyBinding);
        }
        if (handoffWebSearch !== null) {
          setWebSearchChoice(handoffWebSearch.provider, handoffWebSearch.enabled);
        }
        // Must be set before append so the adapter picks it up; it resets to
        // user_input after each send, so only the first message is tagged.
        if (initialSource !== undefined) {
          composerState.pendingSource = initialSource;
        }

        if (handoffFiles.length === 0) {
          runtime.thread.append(prompt);
        } else {
          const attachments: Array<ReturnType<typeof uprightAttachmentAdapter.send>> = [];
          for (const file of handoffFiles) {
            try {
              const pending = await uprightAttachmentAdapter.add({ file });
              attachments.push(uprightAttachmentAdapter.send(pending));
            } catch (err) {
              // PLT-6061 deliberately leaves this one console-only, while the
              // picker, paste and landing paths all now report. Two reasons:
              // the file was validated against the same limits by
              // `LandingChatEntry` a moment ago, so the only failure left
              // here is `FileReader` itself; and the composer's notice row —
              // the surface the other paths use — is cleared as the run starts,
              // which is exactly the append happening on the next line. A
              // report here needs a surface that survives a send, i.e. a card
              // in the message stream, which `InitialPromptAutoSender` has no
              // way to inject. Raise a follow-up if it is ever seen in the
              // wild rather than growing a second notice mechanism for it.
              console.error('Failed to attach hand-off file:', err);
            }
          }
          runtime.thread.append({
            role: 'user',
            content: [{ type: 'text', text: prompt }],
            attachments,
          });
        }
      } finally {
        setSeedingInitialPrompt(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Stops assistant-ui from auto-scrolling the viewport against the user's will
 * (run-end iframe resizes, MCP-card expand/collapse, late image loads, etc.).
 *
 * assistant-ui's auto-scroll funnels every write through one chokepoint:
 * `viewport.scrollTo(...)`. We shadow that method on the viewport instance with
 * a guard that calls through only when the user is within 24px of the bottom,
 * neutralising the whole pipeline (runStart/runEnd/resize/mutation) in one spot.
 * User-driven scrolls update scrollTop natively and never hit scrollTo, so
 * they're unaffected. (An earlier "pin scrollTop on a userPaused flag" approach
 * lost several races to the lib and was replaced by this.)
 *
 * Carve-outs: while the user is following the stream (last observed scroll
 * position within 24px of the bottom) we allow scrollTo through; a backup
 * ResizeObserver restores scrollTop after a content resize if it drifted while
 * the user was not following (catches scroll-anchor misfires on the flex-end
 * viewport); and a FOLLOW_THREAD_EVENT on the viewport puts it back into
 * following mode and jumps to the bottom, for code that scrolls on the user's
 * behalf (a card button that sends a message).
 *
 * Targeted by DOM query (.aui-thread-viewport, scoped to .upright-chat) because
 * the viewport is owned by <ThreadPrimitive.Viewport> with no ref to plumb.
 */
function useThreadScrollLockOnUserScroll(rootRef: React.RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Dev-warn on a miss: the likeliest cause is assistant-ui renaming this
    // class on a version bump, which would silently disable the scroll-lock.
    const viewportOrNull = root.querySelector<HTMLDivElement>('.aui-thread-viewport');
    if (!viewportOrNull) {
      if (import.meta.env.DEV) {
        console.warn(
          '[useThreadScrollLockOnUserScroll] .aui-thread-viewport not found inside .upright-chat - ' +
            'scroll-lock disabled. Has the assistant-ui class name changed?',
        );
      }
      return;
    }
    // Re-bind to a non-nullable type; TS narrowing through closures is unreliable.
    const viewport: HTMLDivElement = viewportOrNull;

    // 24px of slack over assistant-ui's own <1px exact-bottom check. Rounded so
    // Safari's independent fractional rounding of the three metrics (already seen
    // as HiDPI fractional-height artifacts in PLT-5529) can't push a genuinely
    // at-bottom viewport just over the threshold.
    const NEAR_BOTTOM_PX = 24;
    function isNearBottom(): boolean {
      return Math.round(viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight) < NEAR_BOTTOM_PX;
    }

    // Whether auto-scroll should keep following the stream. Updated ONLY from real
    // scroll events (see onScroll), never recomputed at scrollTo-call time: during
    // streaming the content grows (scrollHeight jumps) a frame before the lib's
    // catch-up scrollTo runs, so a live bottom-gap check would momentarily read the
    // size of the just-appended chunk. On Chrome each chunk is a few px; Safari
    // batches ResizeObserver/scroll delivery so one chunk can exceed NEAR_BOTTOM_PX,
    // which latched the old live gate off for the rest of the response (PLT-5760).
    // Seeded true: a fresh mount is snapped to the bottom by useInitialScrollToBottom,
    // and letting the lib's scroll through before the first user scroll only helps.
    let following = true;

    // (1) Hijack scrollTo on the viewport instance. Bind the prototype impl
    // first so the wrapper can pass through without recursing into itself. Typed
    // permissive so it forwards both scrollTo overloads (the single-arg form is
    // the one assistant-ui calls).
    const originalScrollTo = Element.prototype.scrollTo.bind(viewport) as (...args: unknown[]) => void;

    // Preserved and restored exactly on cleanup (some test harnesses install a
    // scrollTo shim).
    const previousDescriptor = Object.getOwnPropertyDescriptor(viewport, 'scrollTo');

    // Set around our own restore write so the shadow bypasses the gate for it.
    let bypassGate = false;

    function gatedScrollTo(...args: unknown[]): void {
      if (bypassGate) {
        bypassGate = false;
        originalScrollTo(...args);
        return;
      }
      // Block only when the user has scrolled away to read mid-stream
      // (assistant-ui's auto-scroll is the only realistic caller in this surface).
      if (!following) {
        return;
      }
      originalScrollTo(...args);
    }

    Object.defineProperty(viewport, 'scrollTo', {
      configurable: true,
      enumerable: false,
      writable: true,
      value: gatedScrollTo,
    });

    // (2) ResizeObserver fence for the residual cases that move the scroll
    // position without going through scrollTo: browser scroll-anchoring on the
    // flex-end viewport, or a descendant scrollIntoView. Snapshot scrollTop on
    // user scroll, restore it after a resize when the user isn't near-bottom.
    let savedScrollTop = viewport.scrollTop;

    // Follow requests (FOLLOW_THREAD_EVENT), sent when a card button posts a
    // message for the user. They stay in force until the viewport reaches the
    // bottom or the user scrolls up. Not one-shot: the jump lands on the old
    // bottom, and the new message's growth can reach onScroll before the
    // run-start scroll does, which would read as "scrolled away" and let the
    // ResizeObserver below pull the viewport back.
    let followRequested = false;
    function onFollowRequest(): void {
      followRequested = true;
      following = true;
      originalScrollTo({ top: viewport.scrollHeight, behavior: 'instant' });
    }
    viewport.addEventListener(FOLLOW_THREAD_EVENT, onFollowRequest);

    function onScroll(): void {
      // Fires on user drag/wheel/keyboard scrolls and on the lib's scrollTo
      // landing alike, but NOT on pure content growth (scrollTop is unchanged
      // then). So the post-scroll position is an honest read of whether the user
      // is still at the bottom, and the right restore target either way.
      const nearBottom = isNearBottom();
      // Every scroll a follow request causes goes down, so one going up is the user's.
      if (nearBottom || viewport.scrollTop < savedScrollTop - 1) followRequested = false;
      savedScrollTop = viewport.scrollTop;
      following = followRequested || nearBottom;
    }
    viewport.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => {
      if (following) {
        savedScrollTop = viewport.scrollTop;
        return;
      }
      // Defer to the next frame so we run after any synchronous scroll writes on
      // this same RO tick (the lib's RO runs in registration order).
      const target = savedScrollTop;
      requestAnimationFrame(() => {
        if (Math.abs(viewport.scrollTop - target) > 1) {
          bypassGate = true;
          originalScrollTo({ top: target, behavior: 'instant' });
        }
      });
    });
    // Observe the first child (where content lives), as assistant-ui's own RO
    // does; observing the viewport gives noisier, geometry-irrelevant callbacks.
    if (viewport.firstElementChild) {
      ro.observe(viewport.firstElementChild);
    } else {
      ro.observe(viewport);
    }

    return () => {
      viewport.removeEventListener('scroll', onScroll);
      viewport.removeEventListener(FOLLOW_THREAD_EVENT, onFollowRequest);
      ro.disconnect();
      // Defensive: a non-writable/non-configurable scrollTo descriptor could make
      // restore throw. Swallowing is fine (the orphaned node is about to be GC'd);
      // dev-warn so a real regression isn't silent.
      try {
        if (previousDescriptor) {
          Object.defineProperty(viewport, 'scrollTo', previousDescriptor);
        } else {
          delete (viewport as { scrollTo?: Element['scrollTo'] }).scrollTo;
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useThreadScrollLockOnUserScroll] failed to restore scrollTo descriptor', err);
        }
      }
    };
  }, [rootRef]);
}

/**
 * Snaps the viewport to the bottom on first mount when the thread already has
 * messages (panel-reopen and /chat full-page entry, PLT-4700). Needed because
 * useThreadScrollLockOnUserScroll's gate blocks the lib's initial
 * scroll-to-bottom: a fresh mount starts at scrollTop 0, far from the bottom. We
 * write scrollTop directly (the gate only shadows scrollTo); after this snap the
 * viewport is in the near-bottom band and the lib's auto-scroll works normally.
 * Two passes (sync + one rAF) catch late layout like MCP iframes. Empty deps so
 * we only snap on the first mount with content, else we'd yank the user to the
 * bottom on every response.
 */
function useInitialScrollToBottom(rootRef: React.RefObject<HTMLDivElement | null>, messageCount: number): void {
  useEffect(() => {
    if (messageCount === 0) return;
    const root = rootRef.current;
    if (!root) return;
    const viewport = root.querySelector<HTMLDivElement>('.aui-thread-viewport');
    if (!viewport) return;

    function snap(): void {
      if (!viewport) return;
      // Direct write: bypasses the scrollTo gate by design (see hook above).
      viewport.scrollTop = viewport.scrollHeight;
    }

    snap();
    const rafId = requestAnimationFrame(snap);
    return () => {
      cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * PLT-5514 (v1b) — live-inject an out-of-band assistant message into the OPEN
 * chat session, no refresh.
 *
 * When the DD product-set generation job persists a "product set ready"
 * assistant message (the v1a write path) into the conversation the user is
 * looking at, the backend publishes a `ConversationMessageEvent`.
 * `useConversationEvents` signals activity — on the live event AND on every
 * (re)connect — and we reconcile: re-import the WHOLE thread from the
 * freshly-persisted messages, the exact path the history-row reopen uses
 * (`buildSeededRepository` + `runtime.thread.import`), which already round-trips
 * the ProductSetCard correctly.
 *
 * Three guards keep the reconcile correct and cheap:
 *   - Active conversation id: the conversation the user is viewing
 *     (`activeConversationId`, set on turn start/finish; falls back to
 *     `composerState.threadId` so the side panel works too). Drives which
 *     conversation we subscribe to and reconcile — but the subscription only
 *     opens once that conversation has a row server-side (first turn finished,
 *     or reopened from history); see the latch below. A reconcile only ever
 *     imports the conversation whose id the thread on screen has; see
 *     `currentConversationId`.
 *   - Nothing-new check: import only when the server holds more messages than
 *     the live thread is showing (persisted row count > runtime thread message
 *     count — a like-for-like comparison, since each persisted row seeds exactly
 *     one thread message). This makes reconcile a no-op when there is nothing to
 *     add — so the catch-up-on-(re)connect never flashes / resets the thread, and
 *     the creating client (already showing its create turn) doesn't double-render.
 *   - Idle gate: never import mid-stream — `import` replaces the whole message
 *     tree, which would clobber an in-flight run. If a run is active when a
 *     reconcile is requested, defer and replay once it finishes.
 *
 * `enabled: false` resolves the conversation id to `null`, which is the switch the
 * hook already has for "no conversation to watch": nothing subscribes and nothing
 * reconciles. Passed as an argument rather than by skipping the hook call, because
 * a conditional hook is not allowed. The prototype route
 * (`/internal/proto/agent-answer`) needs it for two reasons — a fixture-seeded
 * thread has messages but no server-side conversation, so the subscribe would 404
 * and retry; and worse, `composerState.threadId` is module-level state a
 * reviewer's real chat may have set, so the reconcile could fetch THAT
 * conversation and `import` it straight over the fixture.
 */
function useLiveConversationInjection(enabled: boolean): void {
  const runtime = useAssistantRuntime();
  const queryClient = useQueryClient();
  const isRunning = useThread(state => state.isRunning);

  const activeConversationId = useSyncExternalStore(
    subscribeChatHistoryState,
    () => getChatHistoryState().activeConversationId,
  );
  const hasMessages = useThread(state => state.messages.length > 0);
  const viewedConversationId = enabled ? (activeConversationId ?? (hasMessages ? composerState.threadId : null)) : null;

  // Subscribe only once the conversation is known to exist server-side, then
  // stay subscribed across later turns. The row is written fire-and-forget when
  // a turn's stream ends (`persistChatTurn`), so during the FIRST turn of a
  // fresh thread there is nothing to subscribe to: every attempt 410s, the
  // hook burns its retry budget before the row lands, and on a long turn the
  // subscription is dead for the rest of the conversation (PLT-6545). A thread
  // that has messages and is idle has either finished a turn or was reopened
  // from history — both mean the row exists. This is a latch rather than a
  // derived value: the id must be remembered through the runs that follow,
  // when `isRunning` is true again but the row has not gone anywhere.
  const [conversationId, setConversationId] = useState<string | null>(null);
  useEffect(() => {
    setConversationId(prev => {
      if (viewedConversationId === null) return null;
      if (!isRunning && hasMessages) return viewedConversationId;
      // Mid-run: keep an already-latched id, never latch a new one.
      return prev === viewedConversationId ? prev : null;
    });
  }, [viewedConversationId, isRunning, hasMessages]);

  // Set when a reconcile is requested while a run is in progress; replayed once
  // the thread goes idle. A ref (not state) so it doesn't drive re-renders.
  const pendingRef = useRef(false);

  // The thread on screen is always `composerState.threadId`: turns are sent under it, and opening a
  // stored conversation sets it. `activeConversationId` can name another conversation, for example
  // while a reopen is still fetching. Importing that one would put its messages into the thread on
  // screen, and the next turn would send them as history under this thread's id (PLT-7749).
  const currentConversationId = (): string | null => {
    // Read fresh rather than from the render's `activeConversationId`: this also runs after the fetch.
    const active = getChatHistoryState().activeConversationId;
    if (active !== null && active !== composerState.threadId) return null;
    return composerState.threadId || null;
  };

  const importLatestThread = useCallback(async () => {
    const id = currentConversationId();
    if (id === null) return;
    let detail;
    try {
      detail = await fetchChatConversationDetail(queryClient, id);
    } catch {
      // Best-effort: the message still surfaces on reopen. A transient fetch
      // failure here is not worth a toast (the user didn't initiate it).
      return;
    }
    // Bail if the user switched conversations while we fetched.
    if (currentConversationId() !== id) return;
    // Import only when the server holds more messages than the open thread is
    // showing. Compare like-for-like COUNTS: `buildSeededRepository` maps each
    // persisted row to exactly one thread message, so the server row count and
    // the runtime thread's message count are on the same scale.
    //
    // Do NOT compare a per-turn `messageIndex` against `composerState.messageIndex`:
    // `persistChatTurn` writes a turn's user + assistant rows at the SAME
    // `messageIndex`, so `max(messageIndex)` is turn-scaled (≈ turns−1) while
    // `composerState.messageIndex` is set to the ROW count (≈ 2× turns) on reopen
    // (`chat-history-list.tsx`) and below. Mixing the two makes the guard always
    // true after a reopen → the reconcile silently no-ops in exactly the
    // navigate-away-then-reopen case v1b must cover, and the card only reappears
    // on a second manual reopen (the v1a fallback). PLT-5514.
    const shownCount = runtime.thread.getState().messages.length;
    if (detail.messages.length <= shownCount) return;
    let repository: ReturnType<typeof ExportedMessageRepository.fromArray>;
    try {
      repository = buildSeededRepository(detail.messages, id);
    } catch {
      return;
    }
    runtime.thread.import(repository);
    // Mirror the reopen path: the next user turn is logged as the next index of
    // the server-stored conversation.
    composerState.messageIndex = detail.messages.length;
  }, [queryClient, runtime]);

  // Reconcile now, or defer until the thread goes idle if a run is in progress.
  // `isRunning` is read fresh via the closure recreated when it changes, so the
  // SSE hook (which calls this via a ref) always sees the current value.
  const onActivity = useCallback(() => {
    if (isRunning) {
      pendingRef.current = true;
      return;
    }
    void importLatestThread();
  }, [isRunning, importLatestThread]);

  // Drain a deferred reconcile once the thread goes idle.
  useEffect(() => {
    if (isRunning) return;
    if (!pendingRef.current) return;
    pendingRef.current = false;
    void importLatestThread();
  }, [isRunning, importLatestThread]);

  useConversationEvents({ conversationId, onActivity });
}

/**
 * Assistant-message footer: typing indicator + the after-message Sources list
 * (PLT-4946) + the per-message feedback control (PLT-5741). Sources self-gates
 * (renders nothing without citations) and is CSS-hidden on wide viewports, where
 * the citation rail shows instead.
 *
 * The feedback control lives in the footer rather than assistant-ui's built-in
 * action bar because that bar auto-hides (`autohide="not-last"`) and floats,
 * which fights the "always-visible-at-rest" behaviour PLT-5741 requires. The
 * built-in bar is disabled via the `allow*` flags on `assistantMessage` below;
 * the control renders its own copy button (assistant-ui's `ActionBarPrimitive`
 * copy) plus the 👍/👎 pair, so no assistant-ui action is lost.
 */
function AssistantMessageFooter() {
  return (
    <>
      <TypingFooter />
      <MessageSources />
      <ChatFeedbackControl />
    </>
  );
}

/**
 * The empty thread's welcome slot on a surface that wants no welcome screen (PLT-6766).
 *
 * A component rather than leaving `ThreadWelcome` out of the `components` map, because
 * assistant-ui falls back to its own default welcome when the slot is unfilled. Declared at
 * module level so its identity is stable and the Thread does not remount its welcome subtree
 * on every render.
 *
 * It renders a growing spacer rather than nothing, and that is what puts the composer at the
 * foot of the column. The composer is the viewport's own sticky footer and follows whatever the
 * welcome slot leaves above it, so a slot that renders nothing leaves the composer at the top of
 * an empty page. The spacer takes the free space instead. Not `justify-content: flex-end` on the
 * viewport, which would clip the top of the thread once the messages overflow it.
 */
const NoThreadWelcome = () => <div className="upright-chat-empty-spacer" aria-hidden="true" />;

/**
 * Inner chat content. Must be inside AssistantRuntimeProvider to use the
 * assistant-ui hooks. Exported so the side panel can render it directly,
 * bypassing the UprightChat wrapper (which only adds the auto-prompt-sender).
 */
export function UprightChatInner({
  /**
   * Whether to watch the active conversation for out-of-band assistant messages
   * (PLT-5514). Defaults to on: both real surfaces (`/chat` and the side panel)
   * want it. Off only for the fixture-seeded prototype route, which has no
   * server-side conversation to reconcile against — see
   * `useLiveConversationInjection`.
   */
  liveInjection = true,
  /**
   * Whether an empty thread renders the welcome screen — the headline, the coverage lines and
   * the suggestion pills (PLT-6766).
   *
   * On by default, because on every surface that has no launcher of its own the welcome *is*
   * the landing: the standalone `/chat` shell and the side panel both open on it. The unified
   * shell is the exception and passes `false`. There the launcher at `/` already asks "What do
   * you want to analyze?" over the same composer and the same chips, one rail row away, so a
   * second copy of it inside the chat is the duplicate this shell exists to remove. An empty
   * chat there is the conversation view with nothing in it yet.
   */
  showWelcome = true,
}: {
  liveInjection?: boolean;
  showWelcome?: boolean;
}) {
  const messageCount = useThread(state => state.messages.length);
  const hasMessages = messageCount > 0;
  // Non-team users render with MarkdownTextNonTeam, which strips leaked markup
  // tags no directive plugin claimed; team users keep them visible for debugging
  // (PLT-5092).
  const isTeam = useIsTeam();
  const AssistantText = isTeam ? MarkdownText : MarkdownTextNonTeam;
  const rootRef = useRef<HTMLDivElement>(null);

  useThreadScrollLockOnUserScroll(rootRef);
  useInitialScrollToBottom(rootRef, messageCount);
  // PLT-5514 (v1b): live-inject out-of-band assistant messages (DD "product set
  // ready") into the open session without a refresh.
  useLiveConversationInjection(liveInjection);

  return (
    <div className="upright-chat-layout">
      {/*
       * data-empty drives the dot-grid masks in upright-chat-theme.css: on when
       * the thread is empty (welcome surface), off once the first message lands.
       * Driven from messageCount rather than :has(.aui-thread-welcome-message),
       * which was brittle in the side panel (the welcome subtree isn't reliably
       * present during the empty -> first-message transition).
       */}
      <div className="aui-root upright-chat" ref={rootRef} data-empty={hasMessages ? undefined : ''}>
        {/* Self-gates on the team-user + internal-mode invariant; floats absolutely (PLT-4475). */}
        <UprightChatDebugPanel />
        {/*
         * Chat-context chip: above <Thread> so it stays visible across the
         * welcome and active states. Self-gates to null when no entity is bound.
         * Lives outside the thread DOM so auto-scroll doesn't see it as a resize.
         */}
        <div className="upright-chat-context-row">
          <ChatContextCard />
        </div>
        <Thread
          assistantAvatar={{ fallback: 'U' }}
          components={{
            ThreadWelcome: showWelcome ? UprightChatWelcome : NoThreadWelcome,
            Composer: UprightComposer,
          }}
          welcome={{
            message: 'What do you want to analyze?',
            // Suggestions are rendered by UprightChatWelcome with source tracking
            suggestions: WELCOME_SUGGESTIONS,
          }}
          strings={{
            composer: {
              input: {
                placeholder: composerPlaceholder(hasMessages),
              },
            },
          }}
          assistantMessage={{
            // Disable assistant-ui's built-in action bar entirely: the feedback
            // control in the Footer owns copy + 👍/👎 (PLT-5741), and reload/speak
            // were never surfaced here. This stops a duplicate copy button and the
            // always-hidden built-in thumbs (which would be gated off anyway,
            // since the runtime doesn't configure the `feedback` capability).
            allowCopy: false,
            allowReload: false,
            allowSpeak: false,
            allowFeedbackPositive: false,
            allowFeedbackNegative: false,
            // A tool with no UI of its own still has its row in the turn's tool trail (PLT-7912).
            components: { Text: AssistantText, Footer: AssistantMessageFooter, ToolFallback: ToolTrailFallback },
          }}
          tools={CHAT_TOOL_UIS}
        />
      </div>
    </div>
  );
}

/**
 * Main chat component for the /chat route. The backing runtime is mounted once
 * at the app root by ChatRuntimeProvider (PLT-4625), so this is a pure consumer.
 *
 * `initialPrompt` is a one-shot: if provided, it's appended on mount (the user
 * lands with their question already sent). Used by deep-link entry-points (the
 * AI test workbench's "Open in /chat", cmd-clicked welcome pills). `initialSource`
 * tags that first message; both are consulted only for the first message.
 */
export function UprightChat({
  initialPrompt,
  initialSource,
  showWelcome,
}: { initialPrompt?: string; initialSource?: InitialSource; showWelcome?: boolean } = {}) {
  return (
    <>
      {initialPrompt && <InitialPromptAutoSender prompt={initialPrompt} initialSource={initialSource} />}
      <UprightChatInner showWelcome={showWelcome} />
    </>
  );
}
